import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';

const API_BASE = 'https://collabbay.com/APIControllers';

const PublicBooking = ({ route }) => {
  const collabID = route?.params?.CollabID || '';
  const brandNameFromRoute = route?.params?.BrandName || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [plusOnes, setPlusOnes] = useState('1');
  const [booksDate, setBooksDate] = useState('');

  const formValid = useMemo(() => {
    return (
      name.trim() !== '' &&
      email.trim() !== '' &&
      number.trim() !== '' &&
      plusOnes.trim() !== '' &&
      booksDate.trim() !== ''
    );
  }, [name, email, number, plusOnes, booksDate]);

  useEffect(() => {
    if (!collabID) {
      setError('Missing CollabID.');
      setLoading(false);
      return;
    }

    fetchOffer();
  }, [collabID]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${API_BASE}/GetVenueBookingOfferForPublic.php?CollabID=${encodeURIComponent(collabID)}`
      );

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Failed to fetch offer.');
      }

      setOfferData(json.data);
    } catch (err) {
      setError(err.message || 'Failed to load booking page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formValid) {
      Alert.alert('Missing information', 'Please complete all fields.');
      return;
    }

    const partySize = parseInt(plusOnes, 10);

    if (isNaN(partySize) || partySize < 1) {
      Alert.alert('Invalid party size', 'Party size must be at least 1.');
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE}/InsertPublicBooking.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          CollabID: collabID,
          Name: name.trim(),
          Email: email.trim(),
          Number: number.trim(),
          PlusOnes: partySize,
          BooksDate: booksDate.trim(),
        }),
      });

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Failed to submit booking.');
      }

      Alert.alert('Booking submitted', 'Your booking request has been sent successfully.');

      setName('');
      setEmail('');
      setNumber('');
      setPlusOnes('1');
      setBooksDate('');
    } catch (err) {
      Alert.alert('Submission failed', err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centeredCard}>
          <ActivityIndicator size="large" color="#000" />
          <Text style={styles.centeredText}>Loading public booking offer...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !offerData) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centeredCard}>
          <Text style={styles.errorTitle}>This booking page is unavailable</Text>
          <Text style={styles.errorText}>{error || 'Offer not found.'}</Text>
          {!!brandNameFromRoute && (
            <Text style={styles.brandHint}>{brandNameFromRoute}</Text>
          )}
          <TouchableOpacity style={styles.blackButton} onPress={fetchOffer}>
            <Text style={styles.blackButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.pageWrap}>
          <Text style={styles.eyebrow}>Collabbay Public Booking</Text>

          <View style={styles.heroCard}>
            <View style={styles.heroInner}>
              <Text style={styles.brandName}>{offerData.BrandName}</Text>
              <Text style={styles.offerLabel}>Offer</Text>
              <Text style={styles.offerText}>{offerData.VenuePublicOffer}</Text>
            </View>
          </View>

          <View style={styles.formCard}>
            <Text style={styles.formTitle}>Book now</Text>
            <Text style={styles.formSubtitle}>
              Enter your details below. No login or signup needed.
            </Text>

            <TextInput
              style={styles.input}
              placeholder="Your name"
              placeholderTextColor="#999"
              value={name}
              onChangeText={setName}
            />

            <TextInput
              style={styles.input}
              placeholder="Email address"
              placeholderTextColor="#999"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
            />

            <TextInput
              style={styles.input}
              placeholder="Phone number"
              placeholderTextColor="#999"
              value={number}
              onChangeText={setNumber}
              keyboardType="phone-pad"
            />

            <TextInput
              style={styles.input}
              placeholder="Party size"
              placeholderTextColor="#999"
              value={plusOnes}
              onChangeText={setPlusOnes}
              keyboardType="numeric"
            />

            <TextInput
              style={styles.input}
              placeholder="Booking date and time (e.g. 2026-03-28 19:00)"
              placeholderTextColor="#999"
              value={booksDate}
              onChangeText={setBooksDate}
            />

            <TouchableOpacity
              style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
              onPress={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.submitButtonText}>Send booking request</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default PublicBooking;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollContent: {
    padding: 16,
    paddingVertical: 28,
  },
  pageWrap: {
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
  },
  eyebrow: {
    fontSize: 13,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: '700',
    marginBottom: 14,
  },
  centeredCard: {
    margin: 20,
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 28,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  centeredText: {
    marginTop: 12,
    fontSize: 15,
    color: '#555',
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#111',
    marginBottom: 10,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 15,
    color: '#666',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 22,
  },
  brandHint: {
    fontSize: 14,
    color: '#999',
    marginBottom: 18,
  },
  heroCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    overflow: 'hidden',
    marginBottom: 18,
  },
  heroInner: {
    padding: 20,
  },
  brandName: {
    fontSize: 26,
    fontWeight: '900',
    color: '#111',
    marginBottom: 14,
  },
  offerLabel: {
    fontSize: 12,
    fontWeight: '800',
    color: '#777',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  offerText: {
    fontSize: 17,
    lineHeight: 24,
    fontWeight: '700',
    color: '#111',
  },
  formCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 20,
  },
  formTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#111',
    marginBottom: 6,
  },
  formSubtitle: {
    fontSize: 14,
    lineHeight: 21,
    color: '#666',
    marginBottom: 18,
  },
  input: {
    width: '100%',
    backgroundColor: '#f3f3f3',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: Platform.OS === 'web' ? 16 : 14,
    fontSize: 16,
    color: '#111',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#ececec',
    outlineStyle: 'none',
  },
  submitButton: {
    backgroundColor: '#111',
    borderRadius: 18,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 16,
  },
  blackButton: {
    backgroundColor: '#111',
    borderRadius: 14,
    paddingHorizontal: 18,
    paddingVertical: 12,
  },
  blackButtonText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 14,
  },
});